import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Environment, OrbitControls, PerspectiveCamera, useGLTF, useAnimations } from '@react-three/drei'
import * as THREE from 'three'
// Resolve the GLB that sits at project root via bundler URL
const MAIN_URL = new URL('../../main.glb', import.meta.url).href

// Hardcodable scene constants
const CAMERA_POSITION: [number, number, number] = [0, 1 , 1]
const CAMERA_TARGET: [number, number, number] = [0, 1, 0]
const CAMERA_FOV = 80
// Optional: a subtle interaction plane if needed later
const SCREEN_SIZE: { width: number; height: number } = { width: 1.1, height: 0.65 }
const SCREEN_OFFSET_Z = 0.1

// Adjusted speeds for smoother animation
const SCRUB_SPEED_WHEEL = 0.005 // Increased from 0.002 for more animation per scroll
const SCRUB_SPEED_TOUCH = 0.009 // Increased from 0.006 for mobile
const SMOOTHING_FACTOR = 0.09 // Lower = smoother but more lag, Higher = more responsive
const MOMENTUM_DAMPING = 0.5 // How quickly momentum dies off (0.9-0.95 is good range)

// Control the material roughness (0 = mirror-like, 1 = very rough)
export const ROUGHNESS_INTENSITY = 1

type ScrollState = { 
  isHoveringModel: boolean
}

const scrollState: ScrollState = { isHoveringModel: false }

function ControllerModel() {
  const groupRef = useRef<THREE.Group>(null)
  const gltf = useGLTF(MAIN_URL) as unknown as { scene: THREE.Object3D; animations: THREE.AnimationClip[] }
  const { actions, names, mixer, clips } = useAnimations(gltf.animations, gltf.scene as any)
  const [playedOnce, setPlayedOnce] = useState(false)
  const roughnessTextureRef = useRef<THREE.Texture | null>(null)
  
  // Animation smoothing references
  const targetTimeRef = useRef<number>(0) // Target animation time to lerp to
  const currentTimeRef = useRef<number>(0) // Current smoothed animation time
  const velocityRef = useRef<number>(0) // Velocity for momentum
  const lastInputTime = useRef<number>(Date.now())
  const animationActionRef = useRef<any>(null)

  useEffect(() => {
    console.log('Available animation clips:', names)
  }, [names])

  // Optionally load a roughness texture from public folder and apply to all PBR materials
  useEffect(() => {
    let isCancelled = false
    const candidateUrls = ['/roughness.jpg', '/roughness.png']
    const loader = new THREE.TextureLoader()

    function tryNext(index: number) {
      if (index >= candidateUrls.length) return
      loader.load(
        candidateUrls[index],
        (tex) => {
          if (isCancelled) return
          tex.wrapS = THREE.ClampToEdgeWrapping
          tex.wrapT = THREE.ClampToEdgeWrapping
          tex.anisotropy = 4
          roughnessTextureRef.current = tex
          applyRoughnessMap()
        },
        undefined,
        () => {
          // Try next candidate on error
          tryNext(index + 1)
        }
      )
    }

    function applyRoughnessMap() {
      const root = gltf.scene as THREE.Object3D
      const roughTex = roughnessTextureRef.current
      if (!root || !roughTex) return
      root.traverse((obj) => {
        // Apply to standard/physical materials only
        const mesh = obj as THREE.Mesh
        const material = (mesh as any).material as THREE.Material | THREE.Material[] | undefined
        const materials = Array.isArray(material) ? material : material ? [material] : []
        materials.forEach((mat) => {
          if ((mat as any).isMeshStandardMaterial || (mat as any).isMeshPhysicalMaterial) {
            const pbr = mat as THREE.MeshStandardMaterial
            pbr.roughnessMap = roughTex
            // Optional: dial in metalness/roughness if needed
            if (typeof (pbr as any).metalness === 'number') {
              pbr.metalness = Math.min(0.5, pbr.metalness ?? 0.2)
            }
            if (typeof (pbr as any).roughness === 'number') {
              // Use global intensity control
              pbr.roughness = THREE.MathUtils.clamp(ROUGHNESS_INTENSITY, 0, 1)
            }
            pbr.needsUpdate = true
          }
        })
      })
    }

    tryNext(0)

    return () => {
      isCancelled = true
    }
  }, [gltf.scene])

  // Initialize animation
  useEffect(() => {
    if (!clips || clips.length === 0) return

    const clip = clips[0]
    let act = actions[clip.name]
    if (!act) {
      act = mixer.clipAction(clip, gltf.scene)
      ;(actions as any)[clip.name] = act
    }
    
    act.reset().play()
    act.setLoop(THREE.LoopRepeat, Infinity)
    act.clampWhenFinished = false
    act.enabled = true
    act.setEffectiveWeight(1)
    act.setEffectiveTimeScale(0) // freeze; we'll scrub manually
    animationActionRef.current = act
    setPlayedOnce(true)
  }, [clips, actions, mixer, gltf.scene])

  // Smooth animation update on each frame
  useFrame((state, delta) => {
    if (!animationActionRef.current || !clips || clips.length === 0) return

    const clip = clips[0]
    const duration = clip.duration
    
    // Apply momentum when no recent input
    const timeSinceInput = Date.now() - lastInputTime.current
    if (timeSinceInput > 50) { // Only apply momentum after 50ms of no input
      velocityRef.current *= MOMENTUM_DAMPING
      targetTimeRef.current += velocityRef.current * delta * 60 // Normalize for 60fps
    }

    // Ensure target stays within bounds with ping-pong
    const cycle = duration * 2
    if (cycle > 0) {
      let m = targetTimeRef.current
      m = ((m % cycle) + cycle) % cycle
      targetTimeRef.current = m <= duration ? m : (2 * duration - m)
    }

    // Smooth interpolation to target
    currentTimeRef.current = THREE.MathUtils.lerp(
      currentTimeRef.current,
      targetTimeRef.current,
      SMOOTHING_FACTOR
    )

    // Apply the smoothed time to animation
    animationActionRef.current.time = currentTimeRef.current
    mixer.update(0)
  })

  
  // Scroll to scrub animation time and scroll the background website
  useEffect(() => {
    let lastInput: 'wheel' | 'touch' = 'wheel'
    
    function scrub(deltaY: number) {
      // Scroll the background website regardless of hover
      const scroller = document.getElementById('bg-scroll')
      if (scroller) {
        scroller.scrollBy({ top: deltaY, behavior: 'auto' })
      }

      // Also scrub the animation on any scroll/touch
      if (!clips || clips.length === 0) return

      const speed = lastInput === 'touch' ? SCRUB_SPEED_TOUCH : SCRUB_SPEED_WHEEL
      const deltaTime = deltaY * speed
      
      // Update target time with smoothed input
      targetTimeRef.current += deltaTime
      
      // Blend new velocity with existing for smoother transitions
      const newVelocity = deltaTime
      velocityRef.current = velocityRef.current * 0.7 + newVelocity * 0.3 // Smooth velocity blending
      lastInputTime.current = Date.now()
    }

    function onWheel(e: WheelEvent) {
      lastInput = 'wheel'
      const delta = e.deltaY
      scrub(delta)
    }

    window.addEventListener('wheel', onWheel, { passive: true })
    
    // Touch support for mobile scrubbing
    let lastY: number | null = null
    function onTouchStart(e: TouchEvent) {
      lastInput = 'touch'
      if (e.touches && e.touches.length > 0) {
        lastY = e.touches[0].clientY
        velocityRef.current = 0 // Reset velocity on new touch
      }
    }
    
    function onTouchMove(e: TouchEvent) {
      lastInput = 'touch'
      if (e.touches && e.touches.length > 0) {
        const y = e.touches[0].clientY
        if (lastY !== null) {
          const dy = lastY - y // mimic positive deltaY when swiping up
          scrub(dy)
        }
        lastY = y
      }
    }
    
    function onTouchEnd() {
      lastY = null
    }
    
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: true })
    window.addEventListener('touchend', onTouchEnd, { passive: true })
    
    return () => {
      window.removeEventListener('wheel', onWheel as any)
      window.removeEventListener('touchstart', onTouchStart as any)
      window.removeEventListener('touchmove', onTouchMove as any)
      window.removeEventListener('touchend', onTouchEnd as any)
    }
  }, [clips])

  // Not relying on hover anymore but keep handlers no-ops for future
  const onPointerOver = () => {}
  const onPointerOut = () => {}

  return (
    <group ref={groupRef} onPointerOver={onPointerOver} onPointerOut={onPointerOut}>
      <primitive object={gltf.scene as unknown as THREE.Object3D} dispose={null} />
    </group>
  )
}

// Removed embedded website; the background page provides the site while the model overlays it

function Scene() {
  return (
    <>
      <directionalLight position={[3, 5, 3]} intensity={3} color={"white"} castShadow />
      <PerspectiveCamera makeDefault position={CAMERA_POSITION} fov={CAMERA_FOV} />
      <CameraLookAt />
      <group position={[0, .5, .5]} rotation={[0.25,0,0]}>
        <ControllerModel />
      </group>
      <ambientLight intensity={2} color={"red"}/>
      <OrbitControls enablePan={false} enableRotate={false} enableZoom={false} />
    </>
  )
}

function CameraLookAt() {
  const { camera } = useThree()
  useEffect(() => {
    camera.position.set(CAMERA_POSITION[0], CAMERA_POSITION[1], CAMERA_POSITION[2])
    camera.lookAt(CAMERA_TARGET[0], CAMERA_TARGET[1], CAMERA_TARGET[2])
  }, [camera])
  useFrame(() => {
    camera.lookAt(CAMERA_TARGET[0], CAMERA_TARGET[1], CAMERA_TARGET[2])
  })
  return null
}

export function ControllerScene() {
  return (
    <Canvas dpr={[1, 2]} gl={{ antialias: true, alpha: true }} style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
      <Scene />
    </Canvas>
  )
}

useGLTF.preload(MAIN_URL)